/**
 * Stop-session copy, one record per case.
 *
 * Both copy layers — the line above the Leave session button and the
 * confirmation that follows it — are generated from these records so they can
 * never contradict each other.
 *
 * Cases are defined in docs/designs/collaboration-protocol.md § 3.4 and the
 * copy is specified in docs/designs/ux-rules.md § 15e.
 */

/**
 * A: cloud drawing, browser slot empty — user continues on a copy.
 * B: cloud drawing, browser slot holds another drawing — user returns to it.
 * C: session drawing is kept, nothing else is at stake.
 * D: session drawing and another browser drawing both exist; only one survives.
 */
export type StopSessionCase = "cloud-copy" | "cloud-return" | "local-keep" | "local-choose";

/** Which drawing the user chose to keep. Only "local-choose" offers a choice. */
export type StopSessionChoice = "default" | "keep-session" | "keep-browser";

export interface StopSessionCopy {
  /** Explanatory line shown above the Leave session button. */
  line: string;
  title: string;
  /** Confirmation body paragraphs, in order. */
  body: readonly string[];
  confirmLabel: string;
  /** Present only when the case offers a second, equally weighted outcome. */
  alternateLabel?: string;
}

const CLOUD_TAIL =
  "The cloud drawing keeps everything saved so far. You can reopen it from the gallery.";

export const STOP_SESSION_COPY: Readonly<Record<StopSessionCase, StopSessionCopy>> = {
  "cloud-copy": {
    line: "Leaving the session closes this cloud drawing and returns you to a local drawing.",
    title: "Leave session and close this drawing?",
    body: ["You will leave this cloud drawing and continue in a local copy of it.", CLOUD_TAIL],
    confirmLabel: "Leave session",
  },
  "cloud-return": {
    line: "Leaving the session closes this cloud drawing and returns you to the drawing already in this browser.",
    title: "Leave session and close this drawing?",
    body: [
      "You will leave this cloud drawing and return to the drawing already in this browser.",
      CLOUD_TAIL,
    ],
    confirmLabel: "Leave session",
  },
  "local-keep": {
    line: "Leaving the session disconnects you from live collaboration. The drawing stays in this browser.",
    title: "Leave collaboration session?",
    body: [
      "You will leave this collaboration session. The drawing stays in this browser, including changes made during the session.",
    ],
    confirmLabel: "Leave session",
  },
  "local-choose": {
    line: "Leaving the session disconnects you from live collaboration. This browser already has a drawing, so you will choose which one to keep.",
    title: "Keep which drawing?",
    body: [
      "This session's drawing is not saved anywhere. This browser already has a different drawing.",
      "Keeping one replaces the other.",
    ],
    confirmLabel: "Session drawing",
    alternateLabel: "Browser drawing",
  },
};

export interface StopSessionCaseInput {
  /** The session is backed by a cloud drawing rather than an ephemeral room. */
  isFileBacked: boolean;
  /** The user joined by opening a room URL rather than starting from a local scene. */
  isRoomUrlJoin: boolean;
  /** The single browser scene slot currently holds a non-empty drawing. */
  browserSlotHasDrawing: boolean;
}

/**
 * Resolve which stop-session case applies.
 *
 * The determinant is whether the browser scene slot holds a drawing that is
 * *not* this session's drawing. Scene-only autosave writes that slot for a
 * session started from a local scene, so there the slot always tracks the
 * session and nothing is at stake. Autosave is disabled in file-backed mode and
 * suppressed for room-URL joins, so in those cases a non-empty slot is a
 * different drawing.
 */
export function resolveStopSessionCase({
  isFileBacked,
  isRoomUrlJoin,
  browserSlotHasDrawing,
}: StopSessionCaseInput): StopSessionCase {
  if (isFileBacked) {
    return browserSlotHasDrawing ? "cloud-return" : "cloud-copy";
  }
  const slotTracksSession = !isRoomUrlJoin;
  if (slotTracksSession) return "local-keep";
  return browserSlotHasDrawing ? "local-choose" : "local-keep";
}
